import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import {
  onboardingReviewQueryKey,
  onboardingReviewQueryOptions,
  onboardingStatusQueryKey,
  onboardingStatusQueryOptions,
} from './onboarding-query'
import type { ReviewResponse } from './onboarding-review-contracts'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import layoutStyles from './OnboardingLayout.module.css'
import styles from './ReviewPage.module.css'

const days: Array<[keyof ReviewResponse['clinic']['workingHours'], string]> = [
  ['sun', 'Sunday'],
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
]

function enabledLabel(value: boolean) {
  return value ? 'Enabled' : 'Disabled'
}

function workingHoursLabel(value: string) {
  return value === '00:00-00:00' ? 'Closed' : value.replace('-', ' – ')
}

function setupStatusLabel(
  value: ReviewResponse['communications']['setupStatus']
) {
  const labels = {
    NOT_STARTED: 'Not started',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    NEEDS_ATTENTION: 'Needs attention',
  }
  return labels[value]
}

export function ReviewPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your setup" />
  }
  return <AuthenticatedReviewPage client={client} user={auth.user} />
}

function AuthenticatedReviewPage({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const location = useLocation()
  const query = useQuery(onboardingStatusQueryOptions(client, user))

  if (query.isPending) return <Spinner label="Checking your setup" />
  if (query.isError) {
    if (
      query.error instanceof OnboardingApiError &&
      query.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <ReviewLoadError
        title="Setup review could not be loaded"
        description="Retry to restore your authoritative onboarding state."
        onRetry={() => void query.refetch()}
      />
    )
  }

  const destination = routeOnboardingResponse(query.data)
  if (destination !== 'review') {
    return <Navigate to={destinationPath(destination)} replace />
  }
  return <ReviewDetails client={client} user={user} />
}

function ReviewLoadError({
  title,
  description,
  onRetry,
}: {
  title: string
  description: string
  onRetry: () => void
}) {
  return (
    <div className={styles.stack}>
      <StatePanel kind="error" title={title} description={description} />
      <div className={layoutStyles.actions}>
        <Button type="button" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  )
}

function ReviewDetails({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const query = useQuery(onboardingReviewQueryOptions(client, user))
  const submissionStarted = useRef(false)
  const [working, setWorking] = useState(false)
  const [feedback, setFeedback] = useState<
    'conflict' | 'incomplete' | 'error' | null
  >(null)

  if (query.isPending) return <Spinner label="Loading your clinic setup" />
  if (query.isError) {
    if (
      query.error instanceof OnboardingApiError &&
      query.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    const incomplete =
      query.error instanceof OnboardingApiError &&
      query.error.code === 'review-incomplete'
    return (
      <ReviewLoadError
        title={
          incomplete
            ? 'Your setup needs a correction'
            : 'Setup review could not be loaded'
        }
        description={
          incomplete
            ? 'Flio could not verify all saved setup details. Retry once, then contact Flio support if this continues.'
            : 'Retry to load the latest server-verified setup.'
        }
        onRetry={() => void query.refetch()}
      />
    )
  }

  const review = query.data

  async function confirm() {
    if (submissionStarted.current) return
    submissionStarted.current = true
    setWorking(true)
    setFeedback(null)
    try {
      const completed = await client.completeReview(user, review.revision)
      queryClient.setQueryData(onboardingReviewQueryKey(user.uid), completed)
      const status = await client.getStatus(user)
      queryClient.setQueryData(onboardingStatusQueryKey(user.uid), status)
      void navigate(destinationPath(routeOnboardingResponse(status)), {
        replace: true,
      })
    } catch (error) {
      if (
        error instanceof OnboardingApiError &&
        error.code === 'revision-conflict'
      ) {
        setFeedback('conflict')
        await query.refetch()
      } else if (
        error instanceof OnboardingApiError &&
        error.code === 'review-incomplete'
      ) {
        setFeedback('incomplete')
      } else {
        setFeedback('error')
      }
    } finally {
      submissionStarted.current = false
      setWorking(false)
    }
  }

  return (
    <div className={styles.stack}>
      <header className={styles.header}>
        <h1>Review your clinic setup</h1>
        <p>Confirm these saved details before Flio prepares your clinic.</p>
      </header>

      <Card className={styles.section}>
        <h2>Clinic</h2>
        <dl className={styles.details}>
          <div>
            <dt>Clinic name</dt>
            <dd>{review.clinic.name}</dd>
          </div>
          <div>
            <dt>Address</dt>
            <dd>{review.clinic.address}</dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{review.clinic.phone}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{review.clinic.email}</dd>
          </div>
          {review.clinic.emergencyPhone && (
            <div>
              <dt>Emergency phone</dt>
              <dd>{review.clinic.emergencyPhone}</dd>
            </div>
          )}
          <div>
            <dt>Time zone</dt>
            <dd>{review.clinic.timeZone}</dd>
          </div>
        </dl>
        <h3>Business hours</h3>
        <dl className={styles.hours}>
          {days.map(([key, label]) => (
            <div key={key}>
              <dt>{label}</dt>
              <dd>{workingHoursLabel(review.clinic.workingHours[key])}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card className={styles.section}>
        <h2>Scheduling</h2>
        <dl className={styles.details}>
          <div>
            <dt>Scheduling connection</dt>
            <dd>{review.nexHealth.connected ? 'NexHealth connected' : 'PMS intentionally disabled'}</dd>
          </div>
          <div>
            <dt>Location</dt>
            <dd>{review.nexHealth.selectedLocationName}</dd>
          </div>
          <div>
            <dt>Available providers</dt>
            <dd>{review.scheduling.usableProviderCount}</dd>
          </div>
          {review.scheduling.defaultProviderName && (
            <div>
              <dt>Default provider</dt>
              <dd>{review.scheduling.defaultProviderName}</dd>
            </div>
          )}
          <div>
            <dt>New appointment booking</dt>
            <dd>{enabledLabel(review.scheduling.newAppointmentBooking)}</dd>
          </div>
        </dl>
        <h3>Appointment types</h3>
        <ul className={styles.list}>
          {review.scheduling.appointmentTypes.map((appointmentType) => (
            <li key={`${appointmentType.name}-${appointmentType.minutes}`}>
              <span>{appointmentType.name}</span>
              <span>{appointmentType.minutes} minutes</span>
            </li>
          ))}
        </ul>
        <p className={styles.note}>{review.nexHealth.connected ? 'Scheduling checks passed.' : 'Clinic hours are configured. Appointment and PMS actions are unavailable.'}</p>
      </Card>

      <Card className={styles.section}>
        <h2>Communications</h2>
        <dl className={styles.details}>
          <div>
            <dt>Text messaging</dt>
            <dd>{enabledLabel(review.communications.sms)}</dd>
          </div>
          <div>
            <dt>Voice</dt>
            <dd>{enabledLabel(review.communications.voice)}</dd>
          </div>
          {review.communications.areaCode && (
            <div>
              <dt>Preferred area code</dt>
              <dd>{review.communications.areaCode}</dd>
            </div>
          )}
          {review.communications.assignedPublicNumber && (
            <div>
              <dt>Flio number</dt>
              <dd>{review.communications.assignedPublicNumber}</dd>
            </div>
          )}
          {review.communications.voice && (
            <>
              <div>
                <dt>Call handling</dt>
                <dd>Ava answers every call; front desk transfer on request</dd>
              </div>
              {review.communications.forwardingNumber && (
                <div>
                  <dt>Front desk phone</dt>
                  <dd>{review.communications.forwardingNumber}</dd>
                </div>
              )}
            </>
          )}
          <div>
            <dt>Setup status</dt>
            <dd>{setupStatusLabel(review.communications.setupStatus)}</dd>
          </div>
        </dl>
      </Card>

      <Card className={styles.section}>
        <h2>What happens next</h2>
        <p>
          Flio will prepare your clinic configuration after you confirm. Text
          messaging and Voice are not activated by this review; provider setup
          and validation happen in later stages.
        </p>
      </Card>

      {feedback === 'conflict' && (
        <StatePanel
          kind="error"
          title="Setup changed before confirmation"
          description="The latest saved details are shown. Review them again before confirming."
        />
      )}
      {feedback === 'incomplete' && (
        <StatePanel
          kind="error"
          title="Your setup needs a correction"
          description="Flio could not verify all saved setup details. Contact Flio support before confirming."
        />
      )}
      {feedback === 'error' && (
        <StatePanel
          kind="error"
          title="Confirmation did not complete"
          description="Your setup was not approved. Retry when the service is available."
        />
      )}

      <div className={layoutStyles.actions}>
        <Button type="button" disabled={working} onClick={() => void confirm()}>
          {working ? 'Confirming…' : 'Confirm setup'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => void auth.logout()}
        >
          Sign out
        </Button>
      </div>
    </div>
  )
}
